import React, { useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import TcButton from '../TcButton';
import TcPaper from '../TcPaper';
import TcImage from '../TcImage';
import LoadingScreen from '../shared/LoadingScreen';
import { useDeviceDetection } from '../../hooks/useDeviceDetection';
import { useSimpleImagePreloader } from '../../hooks/useImagePreloader';
import TcScanQrCodeBar from '../TcScanQrCodeBar';
import TcPopupR65 from '../TcPopupR65';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { store } from '../../store';
import { setExperienceLive } from '../../store/slices/exhibitSlice';
import { usePeranakanDesignZone } from '../../hooks/useZoneStatus';

import imagePeranakan00 from '../../assets/images/exhibit/peranakan/img_peranakan_00.jpg';
import imagePeranakan01 from '../../assets/images/exhibit/peranakan/img_peranakan_01.jpg';
import imagePeranakan02 from '../../assets/images/exhibit/peranakan/img_peranakan_02.jpg';
import imagePeranakan03 from '../../assets/images/exhibit/peranakan/img_peranakan_03.jpg';

// Interface for claim result (Redux-only version)
interface ClaimResult {
    type: string;
    payload?: any;
    error?: any;
}

// Helper function to get visitor ID with fallback (Redux-only version)
const getVisitorIdWithFallback = (): string | null => {
    // Priority: Redux -> localStorage -> generate anonymous ID
    try {
        const state = store.getState();
        const reduxVisitorId = state.user.visitorId;
        if (reduxVisitorId) return reduxVisitorId;
    } catch (error) {
        console.warn('Could not access Redux store for visitor ID:', error);
    }
    
    const localStorageVisitorId = localStorage.getItem('visitorId');
    if (localStorageVisitorId) return localStorageVisitorId;
    
    const anonymousId = `anon_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('visitorId', anonymousId);
    return anonymousId;
};


export default function PeranakanDesign() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const dispatch = useAppDispatch();

    // Get current user's visitorId from Redux (with fallback)
    const { visitorId: reduxVisitorId, name, email, isAuthenticated, hasCompletedRegistration } = useAppSelector(state => state.user);
    
    // Use enhanced fallback strategy for visitor ID
    const visitorId = getVisitorIdWithFallback() || undefined;

    // Zone status management for Peranakan Design zone (10.3)
    const {
        zoneStatus,
        isLoading: zoneLoading,
        error: zoneError,
        isAvailable,
        isProcessing,
        fetchZoneStatus,
        claimZone,
        updatePayload,
        updateStatus,
        clearError: clearZoneError,
    } = usePeranakanDesignZone(visitorId);

    // State for QR scan processing
    const qrScanClaimInitiated = useRef(false);
    const qrScanProcessedRef = useRef<string | null>(null);
    const [isClaimingZone, setIsClaimingZone] = React.useState(false);

    // Device detection
    const { orientation } = useDeviceDetection();
    const isLandscape = orientation === 'landscape';
    
    // Preload all images when component mounts
    const allImages = [
        imagePeranakan00,
        imagePeranakan01,
        imagePeranakan02,
        imagePeranakan03
    ];

    const { isLoading, isComplete } = useSimpleImagePreloader(allImages);

    const handleSeePatterns = () => {
        // Navigate to PeranakanPatterns
        navigate('/exhibit/peranakan-patterns');
    };

    const handleDiscoverCulture = () => {
        // Navigate to PeranakanCulture
        navigate('/exhibit/peranakan-culture');
    };

    // Zone claiming function for QR scan
    const handleQrScanClaim = useCallback(async () => {
        // Prevent multiple simultaneous claims
        if (qrScanClaimInitiated.current) {
            return;
        }
        
        // Mark claim as initiated immediately to prevent duplicates
        qrScanClaimInitiated.current = true;
        setIsClaimingZone(true); // Show "Claiming zone..." on button
        
        try {
            // Check if user has uuid (visitorId) according to diagram
            if (visitorId) {
                // POST to zone-status/{zone_id}/claim
                const claimResult = await claimZone(visitorId) as ClaimResult;
                
                // Check if the action was fulfilled (successful claim)
                if (claimResult.type === 'zones/claimZoneStatus/fulfilled') {
                    // Zone claimed successfully - dispatch experience live
                    dispatch(setExperienceLive(true));
                    
                    // Auto-navigate to patterns page after successful claim
                    setTimeout(() => {
                        navigate('/exhibit/peranakan-patterns');
                    }, 2000); // 2 second delay to show success
                }
                
            } else {
                // User has no uuid - try to claim zone anyway for anonymous interaction
                const claimResult = await claimZone() as ClaimResult;
            
                // Check if the action was fulfilled (successful claim)
                if (claimResult.type === 'zones/claimZoneStatus/fulfilled') {
                    // Zone claimed successfully - dispatch experience live
                    dispatch(setExperienceLive(true));
                    
                    // Auto-navigate to patterns page after successful claim
                    setTimeout(() => {
                        navigate('/exhibit/peranakan-patterns');
                    }, 2000); // 2 second delay to show success
                }
            }
            
        } catch (error) {
            console.error('Failed to claim zone:', error);
            // Stay on intro page if claim failed with error
            
        } finally {
            // Reset claim flag after a delay to allow for retries
            setTimeout(() => {
                qrScanClaimInitiated.current = false;
                setIsClaimingZone(false); // Reset button text
            }, 3000);
        }
    }, [visitorId, zoneStatus, claimZone, dispatch, navigate]);

    // Handle QR scan navigation - if user scanned QR for zone 10.3, claim the zone and then navigate to patterns page
    useEffect(() => {
        const fromQrScan = searchParams.get('fromQrScan');
        const qrTimestamp = searchParams.get('qrTimestamp');
        
        if (fromQrScan === 'true') {
            // Check if this is a new QR scan by comparing timestamp
            let isNewQrScan = false;
            
            if (qrTimestamp) {
                // If we have a timestamp, check if it's different from the last processed one
                isNewQrScan = qrTimestamp !== qrScanProcessedRef.current;
            } else {
                // If no timestamp, check if no claim is in progress
                isNewQrScan = !qrScanClaimInitiated.current;
            }
            
            if (isNewQrScan) {
                // Mark this timestamp as processed to prevent duplicate processing
                if (qrTimestamp) {
                    qrScanProcessedRef.current = qrTimestamp;
                }
                
                // Clear the parameters from URL to avoid repeated claims
                const newSearchParams = new URLSearchParams(searchParams);
                newSearchParams.delete('fromQrScan');
                newSearchParams.delete('qrTimestamp');
                setSearchParams(newSearchParams, { replace: true });
                
                // Claim the zone first, then auto-navigate to patterns
                handleQrScanClaim();
            }
        }
    }, [searchParams, visitorId, setSearchParams, handleQrScanClaim, zoneStatus]);

    // Show loading state while images are being cached
    if (isLoading) {
        return <LoadingScreen message="Loading images..." />;
    }
    
    // Intro screen
    return (
        <div className={`
            main-content-wrapper bg-black 
            ${isLandscape ? '!pt-8' : '!pt-[112px]'} 
            unselectable 
            has-transition 
        `}>
            <TcPaper classNameStyle="mt-4">
                <h1>{t('exhibits.peranakanDesign.title')}</h1>
            </TcPaper>

            <TcImage classNameStyle="-mt-6 mb-6">
                <img
                    src={imagePeranakan00}
                    alt={t('exhibits.peranakanDesign.title')}
                    className="w-full"
                />
            </TcImage>

            <TcPaper classNameStyle="mb-6">
                <div className="general-content">
                    <h2 
                        className="!text-2xl -mt-24"
                        dangerouslySetInnerHTML={{ __html: t('exhibits.peranakanDesign.subtitle') }} 
                    />
                    <p 
                        dangerouslySetInnerHTML={{ __html: t('exhibits.peranakanDesign.description') }} 
                    />
                </div>
            </TcPaper>
            
            <div className="button-wrapper flex flex-col gap-4 -mt-6 px-6 !max-w-lg has-transition">
                <TcButton
                    handleClick={handleSeePatterns}
                    primary
                    disabled={isClaimingZone}
                >
                    {isClaimingZone ? t('exhibitShared.claimingZone') : t('exhibits.peranakanDesign.seePatterns')}
                </TcButton>
                <TcButton
                    handleClick={handleDiscoverCulture}
                    primary
                    disabled={isClaimingZone}
                >
                    {t('exhibits.peranakanDesign.discoverTheCulture')}
                </TcButton>
            </div>

            <TcScanQrCodeBar classNameStyle="fixed bottom-0 left-0 right-0 z-[20]" />
        </div>
    );
} 